import React from "react";
import AppBar from "@material-ui/core/AppBar";
import IconButton from "@material-ui/core/IconButton";
import List from "@material-ui/core/List";
import ListItem from "@material-ui/core/ListItem";
import ListItemIcon from "@material-ui/core/ListItemIcon";
import ListItemText from "@material-ui/core/ListItemText";
import Popover from "@material-ui/core/Popover";
import Toolbar from "@material-ui/core/Toolbar";
import Typography from "@material-ui/core/Typography";
import {makeStyles} from '@material-ui/core/styles';

import LoupeIcon from '@material-ui/icons/Loupe';
import MenuIcon from "@material-ui/icons/Menu";
import ShareIcon from '@material-ui/icons/Share';
import TwitterIcon from '@material-ui/icons/Twitter';

import {Link as RouterLink, useLocation} from "react-router-dom";

import {Brand, brandStyleKey} from "../Brand";
import {BrandIconAvatar, ChatIconAvatar} from "./ChatIconAvatar";
import {ChatSnapshotSubscriber, stageClient} from "../logic/StageClient";
import {isFourPointPlanLocation} from "../views/ViewFourPointPlan";
import {tweetCurrentLocation} from "../logic/ShareExternally";


const useBrandStyles = makeStyles(Brand.styles);
const useLocalStyles = makeStyles(theme => ({
  appBar: {
    ...Brand.styles.appbar_brand,
  },
  titleMenuButton: {
    marginRight: theme.spacing(1),
  },
  titleChatIcon: {
    marginRight: theme.spacing(2),
    color: Brand.styles.appbar_brand.backgroundColor,
    backgroundColor: 'white',
  },
  titleText: {
    flexGrow: 1,
  },
  titleRootLink: {
    color: Brand.styles.footer_brand.color,
    textDecoration: 'none',
  },
  titleRightIcon: {
    width: theme.spacing(4),
    height: theme.spacing(4),
    color: '#d467e6', //Brand.styles.appbar_brand.backgroundColor,
    backgroundColor: '#87099c',
  },
  popOverPaper: {},
  popOverContents: {
    padding: theme.spacing(2),
  },
}));


// kinds of Popovers
const POP_GREETINGS = 1;
const POP_SHARE = 2;
const POP_TWITTER_NEXT = 3;

export function TopBar(props) {
  const brandClasses = useBrandStyles();
  const classes = useLocalStyles();

  // state
  const location = useLocation();
  const [currentChatSnapshot, setCurrentChatSnapshot] = React.useState<StageTypes.ChatSnapshot | null>(null);
  const [popOver, setPopOver] = React.useState({anchor: null, kind: 0,})

  // pub-sub with stageClient, for the current chat snapshot
  React.useEffect(() => {
    // handle updates from the subscribed object
    const chatSnapshotSubscriber: ChatSnapshotSubscriber = {
      onChatSnapshot: (cs) => setCurrentChatSnapshot(cs),
    }
    stageClient.subscribeToCurrentChat(chatSnapshotSubscriber);
    return () => stageClient.unsubscribeFromCurrentChat(chatSnapshotSubscriber);
  }, [location]);

  // theme of the background: dependent on the chat snapshot
  const path = location.pathname;
  const isChat = path && path.startsWith('/chat/');
  const isChatComplete = isChat && currentChatSnapshot;
  // const barColorClassName = brandClasses[brandStyleKey(isChatComplete ? currentChatSnapshot.theme : null)];
  const barColorClassName = brandClasses[brandStyleKey(null)];

  // title string
  let topic = ''; // path;
  if (path === '/') topic = Brand.rootPageSecondary;
  else if (path.startsWith('/about')) topic = 'About';
  else if (path.startsWith('/terms')) topic = 'Terms';
  else if (isFourPointPlanLocation(path)) topic = 'Go Fund Yourself';
  else if (isChat) topic = isChatComplete ? currentChatSnapshot.topic : 'Chat Room Loading';

  // change the window title
  document.title = Brand.appName + (topic ? ' - ' + topic : '');

  // pop-over code
  const popOverOpen = popOver.anchor !== null;
  const popOverId = popOverOpen ? 'toolbar-popover' : undefined;
  const openPopOver = (kind, anchor) => setPopOver({anchor: anchor, kind: kind});
  const openChatPopOver = (anchor) => {
    if (currentChatSnapshot && currentChatSnapshot.announcer === '#top')
      setPopOver({anchor: anchor, kind: POP_TWITTER_NEXT});
  }
  const closePopOver = () => setPopOver({anchor: null, kind: popOver.kind});

  return (<React.Fragment>
    <AppBar position="fixed" className={barColorClassName}>
      <Toolbar>
        <IconButton color="inherit" edge="start" aria-label="open drawer" className={classes.titleMenuButton} onClick={props.onOpenDrawer}>
          <MenuIcon/>
        </IconButton>
        {isChatComplete && <ChatIconAvatar chatSnapshot={currentChatSnapshot} noInlineStyle={true} className={classes.titleChatIcon}
                                           onClick={e => openChatPopOver(e.currentTarget)}/>}
        <Typography component="h1" variant="h6" noWrap className={classes.titleText}>
          {!isChat &&
          <RouterLink to="/" className={classes.titleRootLink}>
            {Brand.appName}&nbsp;
            {topic && '- '}
          </RouterLink>}
          {topic}
        </Typography>
        {!isChat && <BrandIconAvatar aria-describedby={popOverId} className={classes.titleRightIcon}
                                     onClick={e => openPopOver(POP_GREETINGS, e.currentTarget)}/>}
        {isChatComplete &&
        <IconButton color="inherit" aria-label="share" onClick={e => openPopOver(POP_SHARE, e.currentTarget)}>
          <ShareIcon/>
        </IconButton>}
      </Toolbar>
    </AppBar>

    <Popover id={popOverId} open={popOverOpen} anchorEl={popOver.anchor} onClose={() => closePopOver()}
             anchorOrigin={{vertical: 'bottom', horizontal: 'center',}}
             transformOrigin={{vertical: 'top', horizontal: 'right',}}
             classes={{paper: classes.popOverPaper}}>

      {/* Greetings Popover*/}
      {popOver.kind === POP_GREETINGS &&
      <Typography className={classes.popOverContents} style={{fontWeight: 500}}>
        Greetings, I am<br/>
        <span style={{fontWeight: 600}}>A.W.E.S.O.M.O.</span>
      </Typography>}

      {/* Share Popover */}
      {popOver.kind === POP_SHARE &&
      <List>
        <ListItem>
          <ListItemText primary="Sharing is caring."/>
        </ListItem>
        <ListItem aria-label="share on twitter" button onClick={() => tweetCurrentLocation()}>
          <ListItemIcon>
            <TwitterIcon color="primary"/>
          </ListItemIcon>
          <ListItemText primary="Tweet this Chat!"/>
        </ListItem>
      </List>}

      {/* Twitter-Next Popover */}
      {popOver.kind === POP_TWITTER_NEXT &&
      <List>
        <ListItem>
          <ListItemText primary="Chat Options"/>
        </ListItem>
        <ListItem aria-label="see upcoming tweets" button component={RouterLink} to='/queue/twitter-next' onClick={() => closePopOver()}>
          <ListItemIcon>
            <LoupeIcon color="primary"/>
          </ListItemIcon>
          <ListItemText primary="See upcoming tweets"/>
        </ListItem>
      </List>}
    </Popover>
  </React.Fragment>);
}
